import { AuthContext } from "../../context/AuthContext";
import { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
    Table,
    Typography,
    Spin,
    Button,
    Modal,
    Input,
    Row,
    Col,
    DatePicker,
    Select,
    Tag
} from "antd";
import {
    CheckOutlined,
    CloseOutlined,
} from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";

const { Option } = Select;

export default function RfcHr() {
    const { authData, backUrl } = useContext(AuthContext);
    const [approvalStatusFilter, setApprovalStatusFilter] = useState("Pending")

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [submitting, setSubmitting] = useState(false);


    // Modals
    const [approveModal, setApproveModal] = useState({ visible: false, id: null });
    const [rejectModal, setRejectModal] = useState({ visible: false, id: null });
    const [approveRemarks, setApproveRemarks] = useState("");
    const [rejectionReason, setRejectionReason] = useState("");

    // Dropdown data
    const [Organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    // Dependent filters
    const [organisationFilter, setOrganisationFilter] = useState("");
    const [departmentFilter, setDepartmentFilter] = useState("");
    const [designationFilter, setDesignationFilter] = useState("");

    // Main filters
    const [filters, setFilters] = useState({
        employee_code: "",
        employee_name: "",
        from_date: "",
        to_date: "",
        status: "",
        organisation: "",
        department: "",
        designation: ""
    });

    // Load Organisations
    const fetchOrganisation = async () => {
        const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        });
        setOrganisations(res.data.results || []);
    };

    const fetchDepartments = async (orgId) => {
        const res = await axios.get(
            `${backUrl}/api/emp/departments/?organisation=${orgId}`,
            { headers: { Authorization: `Bearer ${authData?.access}` } }
        );
        setDepartments(res.data.results || []);
    };

    const fetchDesignations = async (deptId) => {
        const res = await axios.get(
            `${backUrl}/api/emp/designations/?department=${deptId}`,
            { headers: { Authorization: `Bearer ${authData?.access}` } }
        );
        setDesignations(res.data.results || []);
    };

    useEffect(() => {
        fetchOrganisation();
    }, []);

    // Fetch RFC Data
    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backUrl}/api/hr/attendance-approvals/?is_employee_view=false`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            const fetched = res.data.results || res.data || [];
            setData(fetched);
            setFilteredData(fetched);
        } catch {
            toast.error("Failed to fetch data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // ==========================================
    // FILTERING LOGIC
    // ==========================================
    useEffect(() => {
        let result = [...data];

        // Employee Code
        if (filters.employee_code) {
            result = result.filter((item) =>
                item.employee_code?.toLowerCase().includes(filters.employee_code.toLowerCase())
            );
        }

        // Employee Name
        if (filters.employee_name) {
            result = result.filter((item) =>
                item.employee_name?.toLowerCase().includes(filters.employee_name.toLowerCase())
            );
        }

        // Date Filters
        if (filters.from_date && filters.to_date) {
            result = result.filter((item) => {
                const date = dayjs(item.attendance_date);
                return (
                    date.isAfter(dayjs(filters.from_date).subtract(1, "day")) &&
                    date.isBefore(dayjs(filters.to_date).add(1, "day"))
                );
            });
        } else if (filters.from_date) {
            result = result.filter((item) =>
                dayjs(item.attendance_date).isSameOrAfter(filters.from_date, "day")
            );
        } else if (filters.to_date) {
            result = result.filter((item) =>
                dayjs(item.attendance_date).isSameOrBefore(filters.to_date, "day")
            );
        }

        // Status
        if (approvalStatusFilter) {
            result = result.filter((item) =>
                item.status?.toLowerCase().includes(approvalStatusFilter.toLowerCase())
            );
        }

        // Organisation
        if (filters.organisation) {
            result = result.filter((item) =>
                item.organisation?.toLowerCase() === filters.organisation.toLowerCase()
            );
        }

        // Department
        if (filters.department) {
            result = result.filter((item) =>
                item.department?.toLowerCase() === filters.department.toLowerCase()
            );
        }

        // Designation
        if (filters.designation) {
            result = result.filter((item) =>
                item.designation?.toLowerCase() === filters.designation.toLowerCase()
            );
        }

        setFilteredData(result);
    }, [filters, data, approvalStatusFilter]);

    // ==========================================
    // APPROVE / REJECT
    // ==========================================
    const handleApprove = async (id) => {
        try {
            setSubmitting(true);

            setApproveModal({ visible: true, id });
            setApproveRemarks("");

        } catch (error) {
            console.error("Approval failed:", error);
        } finally {
            setSubmitting(false);
        }
    };


    const handleReject = (id) => {
        setRejectModal({ visible: true, id });
        setRejectionReason("");
    };

    const submitApprove = async () => {
        if (!approveRemarks.trim()) return toast.warning("Please enter approval remarks");

        try {
            const res = await axios.patch(
                `${backUrl}/api/hr/attendance-approvals/${approveModal.id}/`,
                { status: "Approved", remarks: approveRemarks },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );

            toast.success("Request approved successfully");
            setApproveModal({ visible: false, id: null });
            fetchData();
        } catch (error) {
            toast.error("Failed to approve request");
        }
    };

    const submitReject = async () => {
        if (!rejectionReason.trim()) return toast.warning("Please enter a rejection reason");

        try {
            const res = await axios.patch(
                `${backUrl}/api/hr/attendance-approvals/${rejectModal.id}/`,
                { status: "Reverted", remarks: rejectionReason },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );

            toast.success("Request rejected successfully");
            setRejectModal({ visible: false, id: null });
            fetchData();
        } catch (error) {
            toast.error("Failed to reject request");
        }
    };

    // ==========================================
    // TABLE COLUMNS
    // ==========================================
    const columns = [
        { title: "Employee Code", dataIndex: "employee_code", key: "employee_code" },
        { title: "Employee Name", dataIndex: "employee_name", key: "employee_name" },

        {
            title: "Date",
            dataIndex: "attendance_date",
            key: "attendance_date",
            render: (date) =>
                date ? dayjs(date).format("DD/MM/YYYY") : "-",
        },
        {
            title: "Arrival At",
            dataIndex: ["attendance_details", "first_check_in"],
            key: "attendance_status_message",
            render: (time) => time ? dayjs(time).format("hh:mm A") : "-",
        },
        { title: "Attendance Status", dataIndex: ["attendance_details", "status"], key: "attendance_status" },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (text) => {
                let color = "default";
                if (text === "Approved") color = "green";
                else if (text === "Pending") color = "gold";
                else if (text === "Reverted" || text === "Rejected") color = "red";

                return <p color={color}>{text || "-"}</p>;
            },
        },
        {
            title: "Overall Status",
            dataIndex: "request_status",
            key: "request_status",
            render: (text) => {
                let color = "default";
                if (text === "Approved") color = "green";
                else if (text === "Pending") color = "gold";
                else if (text === "Reverted" || text === "Rejected") color = "red";

                return <p color={color}>{text || "-"}</p>;
            },
        },
        {
            title: "Reason",
            dataIndex: "request_remarks",
            key: "request_remarks",
            render: (text) => text || "-",
        },

        // APPROVAL HISTORY
        {
            title: "Remarks",
            key: "remarks",
            width: 400,
            render: (_, record) => {
                const approval_list = record.approvals || [];

                return (
                    <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                        {approval_list.map((a, index) => (
                            <div key={index} style={{ marginBottom: "10px" }}>
                                <strong>{a.approver_name}: </strong>
                                {a.remarks || "-"}
                                {a.cancellation_reason && (
                                    <div
                                        style={{
                                            color: "red",
                                            background: "#ffe6e6",
                                            padding: "3px",
                                            borderRadius: "6px",
                                            marginTop: "4px",
                                            fontWeight: 600
                                        }}
                                    >
                                        Cancellation: {a.cancellation_reason}
                                    </div>
                                )}
                                {index !== approval_list.length - 1 && (
                                    <hr style={{ border: "0.5px solid #ddd" }} />
                                )}
                            </div>
                        ))}
                    </div>
                );
            },
        },
        { title: "Organisation", dataIndex: "organisation", key: "organisation" },
        { title: "Department", dataIndex: "department", key: "department" },
        { title: "Designation", dataIndex: "designation", key: "designation" },
        // ACTION BUTTONS
        {
            title: "Actions",
            key: "actions",
            width: 150,
            render: (_, record) => (
                <>
                    <Button
                        type="primary"
                        icon={<CheckOutlined />}
                        size="small"
                        style={{
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                            background: "#28a745"
                        }}
                        onClick={() => handleApprove(record.id)}
                    />
                    <Button
                        danger
                        icon={<CloseOutlined />}
                        size="small"
                        style={{
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                            marginLeft: 20
                        }}
                        onClick={() => handleReject(record.id)}
                    />
                </>
            ),
        },
    ];

    return (
        <>
            <Typography.Title level={4} style={{ textTransform: "uppercase" }}>
                Requests for Consideration
            </Typography.Title>

            <div style={{ textAlign: "center", marginBottom: "20px" }}>
                <Button
                    type={approvalStatusFilter === "Pending" ? "primary" : "default"}
                    onClick={() => {
                        setApprovalStatusFilter("Pending");
                    }}
                    style={{
                        marginRight: "10px",
                        backgroundColor: approvalStatusFilter === "Pending" ? "#faad14" : undefined,
                        borderColor: approvalStatusFilter === "Pending" ? "#faad14" : undefined,
                    }}
                >
                    Pending
                </Button>
                <Button
                    type={approvalStatusFilter === "Approved" ? "primary" : "default"}
                    onClick={() => {
                        setApprovalStatusFilter("Approved");
                    }}
                    style={{
                        marginRight: "10px",
                        backgroundColor: approvalStatusFilter === "Approved" ? "#4CAF50" : undefined,
                        borderColor: approvalStatusFilter === "Approved" ? "#4CAF50" : undefined,
                    }}
                >
                    Approved
                </Button>
                <Button
                    type={approvalStatusFilter === "Rejected" ? "primary" : "default"}
                    onClick={() => {
                        setApprovalStatusFilter("Rejected");
                    }}
                    style={{
                        marginRight: "10px",
                        backgroundColor: approvalStatusFilter === "Rejected" ? "#f5222d" : undefined,
                        borderColor: approvalStatusFilter === "Rejected" ? "#f5222d" : undefined,
                    }}
                >
                    Rejected
                </Button>
            </div>

            <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>

                {/* Organisation Filter */}
                <Col xs={24} sm={12} md={6}>
                    <Select
                        placeholder="Select Organisation"
                        style={{ width: "100%" }}
                        value={organisationFilter || undefined}
                        onChange={(value) => {
                            const orgName = Organisations.find(o => o.id === value)?.name || "";

                            setOrganisationFilter(value);
                            setDepartmentFilter("");
                            setDesignationFilter("");

                            setDepartments([]);
                            setDesignations([]);

                            setFilters({
                                ...filters,
                                organisation: orgName,
                                department: "",
                                designation: ""
                            });

                            if (value) fetchDepartments(value);
                        }}
                        allowClear
                    >
                        {Organisations.map((o) => (
                            <Option key={o.id} value={o.id}>{o.name}</Option>
                        ))}
                    </Select>
                </Col>

                {/* Department Filter */}
                <Col xs={24} sm={12} md={6}>
                    <Select
                        showSearch
                        filterOption={(input, option) =>
                            option?.children?.toLowerCase().includes(input.toLowerCase())
                        }
                        placeholder="Select Department"
                        style={{ width: "100%" }}
                        value={departmentFilter || undefined}
                        disabled={!organisationFilter}
                        onChange={(value) => {
                            const deptName = departments.find(d => d.id === value)?.name || "";

                            setDepartmentFilter(value);
                            setDesignationFilter("");

                            setDesignations([]);

                            setFilters({
                                ...filters,
                                department: deptName,
                                designation: ""
                            });

                            if (value) fetchDesignations(value);
                        }}
                        allowClear
                    >
                        {departments.map((d) => (
                            <Option key={d.id} value={d.id}>{d.name}</Option>
                        ))}
                    </Select>
                </Col>

                {/* Designation Filter */}
                <Col xs={24} sm={12} md={6}>
                    <Select
                        showSearch
                        filterOption={(input, option) =>
                            option?.children?.toLowerCase().includes(input.toLowerCase())
                        }
                        placeholder="Select Designation"
                        style={{ width: "100%" }}
                        value={designationFilter || undefined}
                        disabled={!departmentFilter}
                        onChange={(value) => {
                            const desigName = designations.find(ds => ds.id === value)?.name || "";
                            setDesignationFilter(value);
                            setFilters({ ...filters, designation: desigName });
                        }}
                        allowClear
                    >
                        {designations.map((ds) => (
                            <Option key={ds.id} value={ds.id}>{ds.name}</Option>
                        ))}
                    </Select>
                </Col>

                {/* Employee Code */}
                <Col xs={24} sm={12} md={6}>
                    <Input
                        placeholder="Filter by Employee Code"
                        value={filters.employee_code}
                        onChange={(e) =>
                            setFilters({ ...filters, employee_code: e.target.value })
                        }
                    />
                </Col>

                {/* Employee Name */}
                <Col xs={24} sm={12} md={6}>
                    <Input
                        placeholder="Filter by Employee Name"
                        value={filters.employee_name}
                        onChange={(e) =>
                            setFilters({ ...filters, employee_name: e.target.value })
                        }
                    />
                </Col>

                {/* From Date */}
                <Col xs={24} sm={12} md={6}>
                    <DatePicker
                        placeholder="From Date"
                        format="DD/MM/YYYY"
                        value={filters.from_date ? dayjs(filters.from_date) : null}
                        onChange={(date) =>
                            setFilters({
                                ...filters,
                                from_date: date ? date.format("YYYY-MM-DD") : "",
                            })
                        }
                        style={{ width: "100%" }}
                    />
                </Col>

                {/* To Date */}
                <Col xs={24} sm={12} md={6}>
                    <DatePicker
                        placeholder="To Date"
                        format="DD/MM/YYYY"
                        value={filters.to_date ? dayjs(filters.to_date) : null}
                        onChange={(date) =>
                            setFilters({
                                ...filters,
                                to_date: date ? date.format("YYYY-MM-DD") : "",
                            })
                        }
                        style={{ width: "100%" }}
                    />
                </Col>

                {/* Status */}
                <Col xs={24} sm={12} md={6}>
                    <Select
                        placeholder="Filter by Status"
                        value={filters.status || undefined}
                        onChange={(value) => setFilters({ ...filters, status: value || "" })}
                        allowClear
                        style={{ width: "100%" }}
                    >
                        <Option value="Pending">Pending</Option>
                        <Option value="Approved">Approved</Option>
                        <Option value="Rejected">Rejected</Option>
                    </Select>
                </Col>
            </Row>

            {/* TABLE */}
            {loading ? (
                <div style={{ textAlign: "center", marginTop: 50 }}>
                    <Spin size="large" />
                </div>
            ) : (
                <Table
                    dataSource={filteredData}
                    columns={columns}
                    rowKey="id"
                    bordered
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 2500 }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th
                                    {...props}
                                    style={{ backgroundColor: "#0e497b", color: "white" }}
                                >
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            )}

            {/* Reject Modal */}
            <Modal
                title="Reject Request"
                open={rejectModal.visible}
                onOk={submitReject}
                onCancel={() => {
                    setRejectModal({ visible: false, id: null });
                    setRejectionReason("");
                }}
                okText="Reject"
                okButtonProps={{ danger: true }}
            >
                <Input.TextArea
                    rows={3}
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Enter rejection reason"
                />
            </Modal>

            {/* Approve Modal */}
            <Modal
                title="Approve Request"
                open={approveModal.visible}
                onOk={submitApprove}
                confirmLoading={submitting}   // 🔄 Loader here
                onCancel={() => {
                    setApproveModal({ visible: false, id: null });
                    setApproveRemarks("");
                }}
                okText="Approve"
                okButtonProps={{
                    style: {
                        backgroundColor: "green",
                        borderColor: "green",
                        color: "white",
                    },
                }}
            >
                <Input.TextArea
                    rows={3}
                    value={approveRemarks}
                    onChange={(e) => setApproveRemarks(e.target.value)}
                    placeholder="Enter approval remarks"
                />
            </Modal>

            <ToastContainer position="top-right" autoClose={3000} />
        </>
    );
}
